import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/browser';
import ErrorBoundary from 'components/ErrorBoundary';
import Maintenance from 'components/Maintenance';
import { useRouterEvents } from 'hooks/ui/useRouterEvents';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { RootStoreProvider } from 'providers/RootStoreProvider';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import ScriptLayer from 'scripts/ScriptLayer';
import { overrideAnalyticsEnabled } from 'utils/analytics/analytics';
import {
  ENVIRONMENT,
  GOOGLE_CLIENT_ID,
  MAINTENANCE_MODE,
  runningInProduction,
  SkeletonGrey,
  SkeletonWhite,
} from 'utils/constants';
import { setCookiesFromQueryParams } from 'utils/setCookiesFromQueryParams';
import '../asset/css/global.css';
import LoadingOverlay from '../components/layout/LoadingOverlay';
import SEO from '../next-seo.config';
import '../public/montserrat/montserrat.css';

import { SkeletonTheme } from 'react-loading-skeleton';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './main-layout.globals.css';

import './about-us.globals.css';
import DesignSystemWrapper from './design-system/_DesignSystemWrapper';

Sentry.init({
  environment: ENVIRONMENT,
  // A `null` DNS is the official way of disabling the SDK.
  dsn: runningInProduction
    ? 'https://ef2866d0a06245d99d69e16c683dc94c@o386295.ingest.sentry.io/5220387'
    : 'https://5da9b79462deafe41d5900e5dbb9c237@o4506536506753024.ingest.sentry.io/4506536508588032',
  ignoreErrors: [
    'adsbygoogle.push() error: No slot size for availableWidth=0', // Ads work fine, so this (very common) error is not helpful
  ],
});

const reportSentryError = (error: any, errorInfo: any) => {
  Sentry.withScope((scope) => {
    Object.keys(errorInfo).forEach((key) => {
      scope.setExtra(key, errorInfo[key]);
    });

    Sentry.captureException(error);
  });
};

/** Some router events require our stores (e.g. to get a user's identity, therefore we must separate this into a separate
 * component that renders inside the RootStoreProvider
 */
const RouterEventsWatcher = (props: {
  setLoading: Dispatch<SetStateAction<boolean>>;
  children: ReactNode;
}) => {
  const router = useRouter();
  useRouterEvents(router, props.setLoading);
  return props.children;
};

export default function App({ Component, pageProps }: AppProps<any>) {
  const router = useRouter();
  const isDesignSystem = router.pathname.startsWith('/design-system');

  const [generatedDate, _setGeneratedDate] = useState(() => {
    // We want a date on every page to help with debugging caching. We don't want _app.tsx to require a
    // getServerSideProps or getStaticProps as this will cause NextJS to treat every page as server-side rendered.
    // Instead, on the client-side let's fetch the value out of the server-side generated DOM and use that. On the
    // server-side we use the current date. When running in development, the hot-reloading means there won't be a
    // server-side rendered HTML, so just use 'development'
    if (process.env.NODE_ENV === 'development') {
      return 'development';
    }
    if (typeof window !== 'undefined') {
      const dateInDom = document
        .querySelector('#daymade-page-info')
        ?.innerHTML.trim();
      return dateInDom;
    } else {
      return new Date().toISOString();
    }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const emailAddress = localStorage['login_email_address'] || '';

    if (emailAddress !== '') {
      Sentry.setUser({
        email: emailAddress,
      });
    }

    setCookiesFromQueryParams(router.query);
  }, [router.query, router.isReady]);

  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <RootStoreProvider hydrationData={pageProps.hydrationData}>
        <ErrorBoundary reportSentryError={reportSentryError}>
          <RouterEventsWatcher setLoading={setLoading}>
            {loading && <LoadingOverlay />}
            <DefaultSeo {...SEO} />
            {(runningInProduction || overrideAnalyticsEnabled) && (
              <ScriptLayer />
            )}
            <SkeletonTheme
              baseColor={SkeletonGrey}
              highlightColor={SkeletonWhite}
              duration={1.5}
            >
              {router.isReady && isDesignSystem && (
                <DesignSystemWrapper>
                  <Component {...pageProps} />
                </DesignSystemWrapper>
              )}
              {router.isReady && !isDesignSystem && (
                <Component {...pageProps} />
              )}
            </SkeletonTheme>
            {/* Output the time this page was generated. This can help with seeing what pages are cached */}
            <div id="daymade-page-info" data-generated={generatedDate} />
          </RouterEventsWatcher>
        </ErrorBoundary>
      </RootStoreProvider>
    </GoogleOAuthProvider>
  );
}
